import React, { useState } from "react";
import { Columns3, ChevronUp, ChevronDown, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { COLUMNS, COLUMN_BY_KEY, COLUMN_GROUPS, PRESETS } from "@/lib/planColumns";

/** Show, hide and reorder plan columns; also sets the plan's currency and hours per day. */
export const ColumnChooser = ({ open, onOpenChange, selected, onApply, settings, onSaveSettings }) => {
  const [keys, setKeys] = useState(selected);
  const [cfg, setCfg] = useState({ currency: settings?.currency ?? "£", hours_per_day: settings?.hours_per_day ?? 8, track_cost: settings?.track_cost !== false });

  const toggle = (k) => setKeys((ks) => (ks.includes(k) ? ks.filter((x) => x !== k) : [...ks, k]));
  const move = (k, dir) => setKeys((ks) => {
    const i = ks.indexOf(k);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ks.length) return ks;
    const next = [...ks];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="column-chooser-dialog" className="max-h-[88vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Columns</DialogTitle>
          <DialogDescription>
            Choose which fields the plan grid shows and in what order. Your choice is remembered on this
            device and applies to both the Outline and By-stage views.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2" data-testid="column-presets">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Presets</span>
          {Object.keys(PRESETS).map((p) => (
            <button key={p} data-testid={`column-preset-${p.toLowerCase()}`} onClick={() => setKeys(PRESETS[p])}
              className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600 transition-colors hover:bg-slate-900 hover:text-white">
              {p}
            </button>
          ))}
          <button data-testid="column-reset-btn" onClick={() => setKeys(PRESETS.Default)}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-900">
            <RotateCcw size={11} /> Reset
          </button>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              {COLUMN_GROUPS.map((g) => (
                <div key={g}>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{g}</p>
                  <div className="mt-1 space-y-1">
                    {COLUMNS.filter((c) => c.group === g).map((c) => (
                      <label key={c.key} className="flex cursor-pointer items-center gap-2 text-xs text-slate-700">
                        <input data-testid={`column-toggle-${c.key}`} type="checkbox" className="h-3.5 w-3.5"
                          checked={keys.includes(c.key)} onChange={() => toggle(c.key)} />
                        {c.label}
                        {c.cost && !cfg.track_cost && <span className="text-[10px] text-slate-400">(cost off)</span>}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Order shown</p>
              <div className="mt-1 max-h-64 space-y-0.5 overflow-y-auto rounded border border-slate-200 p-1.5" data-testid="column-order-list">
                {keys.map((k, i) => (
                  <div key={k} className="flex items-center justify-between rounded px-1.5 py-0.5 text-xs hover:bg-slate-50">
                    <span>{COLUMN_BY_KEY[k]?.label || k}</span>
                    <span className="flex gap-0.5">
                      <button data-testid={`column-up-${k}`} onClick={() => move(k, -1)} aria-label="Move up" disabled={i === 0} className="text-slate-400 hover:text-slate-900 disabled:opacity-30"><ChevronUp size={12} /></button>
                      <button data-testid={`column-down-${k}`} onClick={() => move(k, 1)} aria-label="Move down" disabled={i === keys.length - 1} className="text-slate-400 hover:text-slate-900 disabled:opacity-30"><ChevronDown size={12} /></button>
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2 border-t border-slate-100 pt-3" data-testid="plan-settings-block">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Plan settings</p>
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input data-testid="track-cost-toggle" type="checkbox" className="h-3.5 w-3.5" checked={cfg.track_cost}
                  onChange={(e) => setCfg({ ...cfg, track_cost: e.target.checked })} />
                Track cost on this plan
              </label>
              <div className="flex items-center gap-2">
                <label className="w-28 text-xs text-slate-500">Currency</label>
                <Input data-testid="currency-input" value={cfg.currency} maxLength={4} className="h-8 w-16"
                  onChange={(e) => setCfg({ ...cfg, currency: e.target.value })} />
              </div>
              <div className="flex items-center gap-2">
                <label className="w-28 text-xs text-slate-500">Hours per day</label>
                <Input data-testid="hours-per-day-input" type="number" step="0.5" min="1" max="24" value={cfg.hours_per_day} className="h-8 w-20"
                  onChange={(e) => setCfg({ ...cfg, hours_per_day: Number(e.target.value) || 8 })} />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" data-testid="column-cancel-btn" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="column-apply-btn" className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}
            onClick={() => { onApply(keys); onSaveSettings(cfg); onOpenChange(false); }}>
            <Columns3 size={14} /> Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
